export const EXAM_ATTEMPT_SUPPORT_EMAIL = 'studentservice@steinbeis-next.de';

export type ExamAttemptTone = 'warning' | 'error';

export interface ExamAttemptAction {
  label: string;
  testID: 'exam-attempts-take-next' | 'exam-attempts-upgrade';
}

export interface ExamAttemptNoteCopy {
  tone: ExamAttemptTone;
  headline: string;
  detail: string | null;
  action: ExamAttemptAction | null;
}

/**
 * Grade-report note. A pass renders nothing. The note does not repeat the grade.
 */
export function describeAttemptScenario(input: {
  result: 'pass' | 'fail';
  used: number;
  allowed: number;
  mode: 'oral' | 'written';
  newerOral: boolean;
  supportEmail: string;
}): ExamAttemptNoteCopy | null {
  if (input.result === 'pass') return null;
  const remaining = input.allowed - input.used;
  const email = input.supportEmail || EXAM_ATTEMPT_SUPPORT_EMAIL;

  if (remaining <= 0) {
    return {
      tone: 'error',
      headline: 'Not passed this time, and all attempts for this module have been used.',
      detail: `Student services can explain your options: ${email}`,
      action: null,
    };
  }
  if (input.mode === 'written' && input.newerOral) {
    return {
      tone: 'warning',
      headline: 'Unfortunately, you did not pass this time. Your next exam will be oral. Reopen the module to view the oral exam and register.',
      detail: null,
      action: {
        label: 'Reopen module',
        testID: 'exam-attempts-upgrade',
      },
    };
  }
  if (input.mode === 'written') {
    return {
      tone: 'warning',
      headline: 'Not passed this time.',
      detail: `Student services can explain how you can repeat this module: ${email}`,
      action: null,
    };
  }
  return {
    tone: 'warning',
    headline: 'Unfortunately, you did not pass this time. Reopen the module to register for another oral exam.',
    detail: null,
    action: { label: 'Reopen module', testID: 'exam-attempts-take-next' },
  };
}

export interface ExamAttemptStatusPayload {
  enabled?: boolean;
  nextAction?: string;
  used?: number | null;
  allowed?: number | null;
  supportEmail?: string | null;
}

export function describeExamAttemptStatus(
  status: ExamAttemptStatusPayload | null | undefined,
): ExamAttemptNoteCopy | null {
  if (!status?.enabled || !status.nextAction || status.nextAction === 'policy_off' || status.nextAction === 'passed') {
    return null;
  }
  const used = status.used ?? 0;
  const allowed = status.allowed ?? used;
  const email = status.supportEmail || EXAM_ATTEMPT_SUPPORT_EMAIL;
  if (status.nextAction === 'take_next_attempt') {
    return describeAttemptScenario({
      result: 'fail', used, allowed, mode: 'oral', newerOral: false, supportEmail: email,
    });
  }
  if (status.nextAction === 'upgrade_to_oral') {
    return describeAttemptScenario({
      result: 'fail', used, allowed, mode: 'written', newerOral: true, supportEmail: email,
    });
  }
  if (status.nextAction === 'needs_student_services') {
    return describeAttemptScenario({
      result: 'fail', used, allowed, mode: 'written', newerOral: false, supportEmail: email,
    });
  }
  if (status.nextAction === 'exhausted') {
    return describeAttemptScenario({
      result: 'fail', used, allowed: used, mode: 'oral', newerOral: false, supportEmail: email,
    });
  }
  return null;
}
